/*
 * Copyright (c) 2017-present, flop! contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 * https://framagit.org/flopedt/flopedt
 */

import type { Ref } from 'vue'
import type { Module, TimeSetting } from '@/stores/declarations.ts'
import type { ModuleAPI } from '@/ts/type.ts'

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { api } from '@/utils/api.ts'

export const usePermanentStore = defineStore('permanent', () => {
  const modules = ref<Module[]>([])
  const intervalMinutes = ref<number>(15)
  const isModulesFetched = ref<boolean>(false)
  const loadingError = ref<Error | null>(null)
  const timeSettings: Ref<Map<number, TimeSetting>> = ref(new Map<number, TimeSetting>())
  const calendarType: Ref<string> = ref('week')
  const areTimeSettingsFetched = ref<boolean>(false)
  const modulesSelected = ref<Module[]>([])
  const moduleColor = computed(() => {
    const moduleColors: Map<number, string> = new Map<number, string>()
    modules.value.forEach((mod: Module) => {
      const colorValue =
        'rgb(' +
        Math.ceil(Math.random() * 155) +
        ',' +
        Math.ceil(Math.random() * 155) +
        ',' +
        Math.ceil(Math.random() * 155) +
        ')'
      moduleColors.set(mod.id, colorValue)
    })
    return moduleColors
  })

  function fetchTimeSettings() {
    ;[...Array(100).keys()].forEach((i: number) => {
      timeSettings.value.set(i, {
        id: i,
        dayStartTime: 480,
        dayEndTime: 1200,
        lunchMinStartTime: 720,
        lunchMaxEndTime: 840,
        lunchDefaultDuration: 60,
        days: ['m', 'tu', 'we'],
        departmentId: i,
      })
    })
    areTimeSettingsFetched.value = true
  }

  async function fetchModules() {
    try {
      await api.getModules().then((result) => {
        result.forEach((mod: ModuleAPI) => {
          modules.value.push({
            id: mod.id,
            name: mod.name,
            abbrev: mod.abbrev,
            headId: mod.head_id,
            url: mod.url,
            trainProgId: mod.train_prog_id,
            trainingPeriodId: mod.training_period_id,
          })
        })
      })
      isModulesFetched.value = true
    } catch (e) {
      loadingError.value = e as Error
    }
  }

  async function getModule(id: number): Promise<Module | undefined> {
    let module = modules.value.find((m) => m.id === id)
    if (!module && !isModulesFetched.value) {
      try {
        await fetchModules()
        module = modules.value.find((m) => m.id === id)
      } catch (error) {
        console.log('Get module failed')
        console.log(error)
      }
    }
    return module
  }

  function clearModules() {
    modules.value = []
    isModulesFetched.value = false
  }

  return {
    modules,
    fetchModules,
    fetchTimeSettings,
    clearModules,
    moduleColor,
    getModule,
    isModulesFetched,
    areTimeSettingsFetched,
    timeSettings,
    intervalMinutes,
    modulesSelected,
    calendarType,
  }
})
